<script setup lang="ts">
import { CheckboxProps, getAriaIDs } from './inputHelper'

const props = defineProps<CheckboxProps>()
const ariaIds = getAriaIDs(props)
const model = defineModel()
const isEmpty = computed(() => model.value === '')
const inputRef = ref<HTMLInputElement | null>(null)

// This is required for accessibility
const handleSwatchKeydown = (event: KeyboardEvent) => {
  if (event.key === ' ') {
    event.preventDefault()
    if (inputRef.value) {
      inputRef.value.checked = !inputRef.value.checked
      inputRef.value.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }
}
</script>

<template>
  <div class="checkbox" :class="{ 'input--not-empty': !isEmpty, 'input--error': error }">
    <label :data-testid="testId">
      <input
        ref="inputRef"
        type="checkbox"
        v-model="model"
        v-bind="$attrs"
        :name
        :required="required"
        :aria-describedby="ariaIds"
        :aria-invalid="!!error"
        :class="{ checkbox__hidden: swatch?.id }"
      />
      <svg-icon
        v-if="swatch?.id"
        :name="`swatches:${getSpriteName({ attrName: swatch?.name, value: swatch?.value, id: swatch.id })}`"
        class="checkbox__swatch"
        tabindex="0"
        :aria-checked="model ? true : false"
        @keydown="handleSwatchKeydown"
      />
      <span>
        <slot>{{ label }}{{ required ? ' *' : '' }}</slot>
      </span>
    </label>
    <InputMessage :name="name" :error="error" :hint="hint" :attempt="attempt" />
  </div>
</template>

<style lang="postcss">
.checkbox {
  padding: var(--space-40);
  padding-left: 0;
  label {
    display: inline-flex;
    align-items: flex-start;
    cursor: pointer;
    gap: var(--space-50);

    input {
      accent-color: var(--surface-dark-10);
      height: 1rem;
      width: 1rem;

      &.checkbox__hidden {
        display: none;
      }

      &:checked + svg.checkbox__swatch {
        outline: var(--space-10) solid var(--ui-dark-10);
        outline-offset: var(--space-10);
        border-radius: 100%;

        &:focus {
          outline: var(--space-10) solid var(--status-info);
          outline-offset: var(--space-10);
          border-radius: 100%;
        }
      }
    }

    .checkbox__swatch {
      height: 1.25rem;
      width: 1.25rem;
      pointer-events: none;
      margin-inline: var(--space-20);
    }
  }
  .input__error {
    padding-left: 1.25rem;
  }
}
</style>
